import { DbHelper, DbObject } from "@app/shared/model/db-object.model";
import { ClassImporterExporter, ImportMap } from "@app/shared/resources/class-importer-exporter.service";

export interface EntityData {
    id: number;
    entitytype_id: number;
    name: string;

    account_id: string | null;
    alula_account_number: string | null;
    billing_address: number | null;
    billing_userinfo: number | null;
    folder_id: number | null;
    fusion_id: string | null;
    notes: string | null;
    parent_id: number | null;
    shipping_address: number | null;
    shipping_userinfo: number | null;
    snapone_code: string | null;
    zoho_id: string | null;
    support_email: string | null;
    support_name: string | null;
    support_phone: string | null;
    support_website: string | null;
}

export interface EntityTreeData {
    id: number;
    entitytype_id: number;
    name: string;

    account_id: string | null;
    parent_id: number | null;
    billing_userinfo: number | null;
    billing_address: number | null;
    shipping_userinfo: number | null;
    shipping_address: number | null;
    fusion_id: number | null;
    notes: string | null;
    alula_account_number: string | null;
    snapone_code: string | null;
    zoho_id: string | null;
    ancestors: number[];
    support_email: string | null;
    support_name: string | null;
    support_phone: string | null;
    support_website: string | null;
}

// export interface IEntity {
//     id: number;
//     entitytypeId: number;
//     name: string;

//     accountId: string | null;
//     alulaAccountNumber: string | null;
//     billingAddress: number | null;
//     billingUserinfo: number | null;
//     folderId: number | null;
//     fusionId: string | null;
//     notes: string | null;
//     parentId: number | null;
//     shippingAddress: number | null;
//     shippingUserinfo: number | null;
//     snaponeCode: string | null;
//     zohoId: string | null;
// }

export class EntityHelper extends DbHelper<Entity, EntityData> {
    dbTypeName = "Company";
    dbTypeNamePlural = "Companies";

    static importMap: ImportMap<Entity, EntityData> = [
        { i: "id", o: "id" },
        { i: "entitytype_id", o: "entityTypeId" },
        { i: "name", o: "name" },
        { i: "account_id", o: "accountId" },
        { i: "alula_account_number", o: "alulaAccountNumber" },
        { i: "billing_address", o: "billingAddress" },
        { i: "billing_userinfo", o: "billingUserinfo" },
        { i: "folder_id", o: "folderId" },
        { i: "fusion_id", o: "fusionId" },
        { i: "notes", o: "notes" },
        { i: "parent_id", o: "parentId" },
        { i: "shipping_address", o: "shippingAddress" },
        { i: "shipping_userinfo", o: "shippingUserinfo" },
        { i: "snapone_code", o: "snaponeCode" },
        { i: "zoho_id", o: "zohoId" },
        { i: "support_email", o: "supportEmail" },
        { i: "support_name", o: "supportName" },
        { i: "support_phone", o: "supportPhone" },
        { i: "support_website", o: "supportWebsite" },
    ];

    readonly importMap = EntityHelper.importMap;

    fromDbObject(entity: EntityData): Entity {
        return ClassImporterExporter.importObject(entity, this.importMap);
    }

    toDbObject(obj: Entity): EntityData {
        return ClassImporterExporter.exportObject(obj, this.importMap);
    }
}

// @TODO: Update this to follow the helper pattern like other models (instead of storing state on a class instance)
export class Entity extends DbObject {
    static dbTypeName = "Company";
    static dbTypeNamePlural = "Companies";

    static importMap = [
        { i: "id", o: "id" },
        { i: "entitytype_id", o: "entityTypeId" },
        { i: "name", o: "name" },
        { i: "account_id", o: "accountId" },
        { i: "alula_account_number", o: "alulaAccountNumber" },
        { i: "billing_address", o: "billingAddress" },
        { i: "billing_userinfo", o: "billingUserinfo" },
        { i: "folder_id", o: "folderId" },
        { i: "fusion_id", o: "fusionId" },
        { i: "notes", o: "notes" },
        { i: "parent_id", o: "parentId" },
        { i: "shipping_address", o: "shippingAddress" },
        { i: "shipping_userinfo", o: "shippingUserinfo" },
        { i: "snapone_code", o: "snaponeCode" },
        { i: "zoho_id", o: "zohoId" },
        { i: "support_email", o: "supportEmail" },
        { i: "support_name", o: "supportName" },
        { i: "support_phone", o: "supportPhone" },
        { i: "support_website", o: "supportWebsite" },
    ] as const;

    static fromDbObject(entity: EntityData): Entity {
        return new Entity(
            entity[Entity.importMap[0].i],
            entity[Entity.importMap[1].i],
            entity[Entity.importMap[2].i],
            entity[Entity.importMap[3].i],
            entity[Entity.importMap[4].i],
            entity[Entity.importMap[5].i],
            entity[Entity.importMap[6].i],
            entity[Entity.importMap[7].i],
            entity[Entity.importMap[8].i],
            entity[Entity.importMap[9].i],
            entity[Entity.importMap[10].i],
            entity[Entity.importMap[11].i],
            entity[Entity.importMap[12].i],
            entity[Entity.importMap[13].i],
            entity[Entity.importMap[14].i],
            entity[Entity.importMap[15].i],
            entity[Entity.importMap[16].i],
            entity[Entity.importMap[17].i],
            entity[Entity.importMap[18].i]
        );
    }

    static fromProps(entity: Entity) {
        // @ts-ignore - ignore missing args
        return Object.assign(new Entity(), entity);
    }

    constructor(
        public id: number,
        public entityTypeId: number,
        public name: string,

        public accountId: string | null,
        public alulaAccountNumber: string | null,
        public billingAddress: number | null,
        public billingUserinfo: number | null,
        public folderId: number | null,
        public fusionId: string | null,
        public notes: string | null,
        public parentId: number | null,
        public shippingAddress: number | null,
        public shippingUserinfo: number | null,
        public snaponeCode: string | null,
        public zohoId: string | null,
        public supportEmail: string | null,
        public supportName: string | null,
        public supportPhone: string | null,
        public supportWebsite: string | null
    ) {
        super();
    }
}

export interface EntityAncestorInt {
    id: number;
    ancestors?: number[];
}

export interface EntityAncestorData {
    entity_id: number;
    ancestors?: number[];
}

export class EntityAncestor extends DbObject {
    static dbTypeName = "EntityAncestor";
    static dbEndpoint = "entity_tree";

    static readonly importMap: ImportMap<EntityAncestorInt, EntityAncestorData> = [];

    static fromDbObject(ancestors: number[]): EntityAncestor {
        return new EntityAncestor(ancestors);
    }

    constructor(public ancestors: number[]) {
        super();
    }
}
